'use strict'

import React, { Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'
import ReactDOM from 'react-dom'

import { getElementAbsolutePosition } from '../../util/html'
import { bind } from '../../util/react'
import FocusAreaBlind from './focus-area-blind'

import styles from './focus-area.module.css'

const propTypes = {
    autofocus: PropTypes.bool,
    blindClassName: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    children: PropTypes.any.isRequired,
    className: PropTypes.string,
    focused: PropTypes.bool,
    onClickOut: PropTypes.func,
    onFocusChanged: PropTypes.func,
    preventTabNavigation: PropTypes.bool,
    unfocusable: PropTypes.bool
}

const defaultProps = {
    focused: false,
    onClickOut: () => {},
    onFocusChanged: () => {}
}

/**
 * A generic FocusArea Component that 'focuses' any content inside either pragmatically or
 * automatically when someone clicks inside of it. The idea behind it is to highlight any content
 * by dimming everything around it. This is achieved by showing a set of 4 overlaying blinds
 * around the focused content.
 *
 * The use of the overlay has an additional benefit of callbacks. You can:
 * - React when the focus changes
 * - Prevent user actions outside of the focused area when focused
 * - Catch user clicking outside
 *
 * Example:
 *     <FocusArea autofocus unfocusable>...</FocusArea>
 * This example will wrap its contents. When someone clicks on the content, it will be
 * highlighted (autofocus). When someone clicks outside, the focus is lost and the overlay hidden
 * (unfocusable)
 *
 * Noteworthy props:
 * - blindClassName: this className will be passed to the overlay, allowing opacity and color changes
 * - focused: pragmatically turn the focus on/off
 * - autofocus: focus the content when the user clicks inside of the FocusArea
 * - unfocusable: unfocus when the user clicks outside of the FocusArea (overlay click)
 * - preventTabNavigation: disable the usage of tabs navigation within the focused area
 * - onClickOut: a callback function that gets fired, when someone clicks outside of the FocusArea
 * - onFocusChanged: a callback that gets fired when the focus state has changed, passing a boolean
 *
 */
class FocusArea extends Component {
    constructor(props) {
        super(props)
        this.blinds = {}
        this.state = { focused: false }
        bind(this)
    }

    componentDidMount() {
        const { focused } = this.props
        window.addEventListener('resize', this.renderBlinds)
        // If the element is mounted with focused = true, in some cases the html element
        // does not have a correct position returned, yet. This is a workaround to fire
        // blinds re-calculation some time after the element has been mounted, just in case.
        this.renderBlinds()
        if (focused) {
            this.setState({ focused: focused }) // eslint-disable-line react/no-did-mount-set-state
        }
    }

    UNSAFE_componentWillUpdate(nextProps) {
        const { onFocusChanged } = this.props
        const { focused } = this.state

        // If focused state changed from above, adapt inner state and callback, if needed.
        if (nextProps.focused !== undefined && nextProps.focused !== focused) {
            this.setState({ focused: nextProps.focused })
            onFocusChanged(nextProps.focused)
        }
    }

    componentDidUpdate() {
        // Re-render blinds in case the content got changed.
        // We need to re-calculate the blinds' positions
        this.renderBlinds()
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.renderBlinds)
        this.removeBlinds()
    }

    render() {
        const { children, className } = this.props
        const { focused } = this.state
        const containerClassName = cx(className, styles['focus-area'], { [styles['inactive']]: !focused })
        return (
            <div className={containerClassName} ref={ref => ref && (this.root = ref)} onClick={this.onAutofocus}>
                {children}
            </div>
        )
    }

    onAutofocus() {
        const { autofocus, onFocusChanged } = this.props
        const { focused } = this.state

        if (autofocus && !focused) {
            this.setState({ focused: true })
            onFocusChanged(true)
        }
    }

    onClickOut() {
        const { onClickOut, onFocusChanged, unfocusable } = this.props

        // If the element is unfocusable, unfocus when clicking outside at the blinds
        if (unfocusable) {
            this.setState({ focused: false })
            onFocusChanged(false)
        }

        onClickOut()
    }

    onTabPress(event) {
        const { focused } = this.state
        // Prevents navigating outside of the focused area with tabs
        if (focused && (event.keyCode === 9 || event.keyCode === 16)) {
            event.preventDefault()
        }
    }

    switchTabNavigation() {
        const { preventTabNavigation } = this.props

        if (preventTabNavigation) {
            if (!this.state.focused) {
                document.removeEventListener('keydown', this.onTabPress)
            } else {
                document.addEventListener('keydown', this.onTabPress)
            }
        }
    }

    removeBlinds() {
        // Remove blinds when the element gets destroyed or the blinds are hidden
        let key
        for (key in this.blinds) {
            if (Object.prototype.hasOwnProperty.call(this.blinds, key)) {
                ReactDOM.unmountComponentAtNode(this.blinds[key])
                document.body.removeChild(this.blinds[key])
            }
        }
        this.blinds = {}
    }

    createBlindElement(positionName) {
        const element = document.createElement('div')
        document.body.appendChild(element)
        this.blinds[positionName] = element
    }

    getBlindElement(positionName) {
        // Get or create a blinds html element and append it to the body,
        // because we need it's absolute position to be relative to the document.

        if (!this.blinds[positionName]) {
            this.createBlindElement(positionName)
        }
        return this.blinds[positionName]
    }

    renderBlind(positionName, style) {
        const { blindClassName } = this.props
        const { focused } = this.state
        const t3selector = `t3sel-focus-area-blind-${positionName}`

        ReactDOM.render(
            <FocusAreaBlind
                onClick={this.onClickOut}
                isShown={focused}
                style={style}
                className={cx(blindClassName, t3selector)}
            />,
            this.getBlindElement(positionName)
        )
    }

    renderBlinds() {
        this.switchTabNavigation()

        // Render 4 blinds around the focused area. This gives us the effect,
        // that everything else is dimmed, while the focused area stays as before.
        const pos = getElementAbsolutePosition(this.root)
        if (!pos.height || !pos.width) {
            return // we are rendering in an "invisible" context (maybe a unit test). Abort.
        }
        this.renderBlind('top', { height: pos.top, left: 0, right: 0, top: 0 })
        this.renderBlind('bottom', { height: pos.bottom, left: 0, right: 0, top: pos.top + pos.height })
        this.renderBlind('left', { height: pos.height, left: 0, top: pos.top, width: pos.left })
        this.renderBlind('right', { height: pos.height, left: pos.left + pos.width, right: 0, top: pos.top })
    }
}

FocusArea.propTypes = propTypes
FocusArea.defaultProps = defaultProps

export default FocusArea
